// ─────────────────────────────────────────────────────────────────────────
// TeamsRequiredBanner.tsx — global banner shown when Microsoft Teams is
// not yet connected. Two variants:
//   - Admin: persistent CTA to Settings → Integrations
//   - Staff: explanation that admin needs to act (dismissable)
//
// Rendered above the main page content in App.tsx, but ONLY on dashboard
// + report pages (not in chat / training / settings — that would be
// noisy and the gate is enforced server-side anyway).
// ─────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';

interface Props {
  isAdmin:           boolean;
  onOpenIntegrations: () => void;
}

const STAFF_DISMISS_KEY = 'cima-teams-banner-staff-dismissed';

export function TeamsRequiredBanner({ isAdmin, onOpenIntegrations }: Props) {
  const [staffDismissed, setStaffDismissed] = useState(
    () => !isAdmin && sessionStorage.getItem(STAFF_DISMISS_KEY) === '1',
  );

  if (!isAdmin && staffDismissed) return null;

  return (
    <div
      role="status"
      style={{
        padding: '12px 24px',
        background: isAdmin
          ? '#0153D4'
          : 'rgba(245,158,11,0.15)',
        color: isAdmin ? 'white' : 'var(--fg)',
        borderBottom: isAdmin ? 'none' : '1px solid rgba(245,158,11,0.30)',
        display: 'flex', alignItems: 'center', gap: 14,
        fontSize: 13.5,
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
        {isAdmin ? '⚡' : '⏳'}
      </span>
      <div style={{ flex: 1, lineHeight: 1.4 }}>
        {isAdmin ? (
          <>
            <strong>Microsoft Teams isn't connected yet.</strong>{' '}
            Until you connect Teams, your team can't raise new incidents.{' '}
            Takes ~15 minutes with your Azure admin.
          </>
        ) : (
          <>
            <strong>Your admin is still setting up the platform.</strong>{' '}
            You can browse existing incidents, complete training, and use the Crisis Advisor —
            but new incidents can't be raised until Microsoft Teams is connected.
          </>
        )}
      </div>
      {isAdmin ? (
        <button
          onClick={onOpenIntegrations}
          style={{
            background: 'rgba(255,255,255,0.18)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.28)',
            borderRadius: 8,
            padding: '7px 14px',
            fontSize: 12.5,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            backdropFilter: 'blur(4px)',
            flexShrink: 0,
          }}
        >
          Set up Microsoft Teams →
        </button>
      ) : (
        <button
          onClick={() => {
            sessionStorage.setItem(STAFF_DISMISS_KEY, '1');
            setStaffDismissed(true);
          }}
          aria-label="Dismiss"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--muted)', fontSize: 18, padding: '0 4px',
            flexShrink: 0,
          }}
        >×</button>
      )}
    </div>
  );
}
